import { Box, Drawer, drawerClasses } from '@mui/material';
import { PropsWithChildren } from 'react';

import { LocalStorageKeys } from '~/common/constants/localStorage';
import { OpenCloseProps } from '~/hooks';
import { useResizeWidth } from '~/hooks/useResizeWidth/useResizeWidth';

import { DrawerResizeHandle } from './components/DrawerResizeHandle/DrawerResizeHandle';

export interface ResizeableDrawerProps extends OpenCloseProps {
  fullHeight?: boolean;
  hasTabs?: boolean;
  /**
   * Minimum width of the drawer. Can be a number (pixels) or a string with px or % suffix.
   */
  minWidth?: number | string;
  /**
   * Maximum width of the drawer. Can be a number (pixels) or a string with px or % suffix.
   */
  maxWidth?: number | string;
}

// Only right anchoring for resizeable drawer is reasonable
// Left side would be partially hidden behind navigation bar
const ANCHOR = 'right';

export const ResizeableDrawer = ({
  open,
  onClose,
  fullHeight = false,
  hasTabs = false,
  children,
  minWidth = 300,
  maxWidth = '75%',
}: PropsWithChildren<ResizeableDrawerProps>) => {
  const { width, isResizing, enableResize } = useResizeWidth({
    // Need to be exposed and set dynamically to support multiple drawers with different widths
    key: LocalStorageKeys.RESIZE_DRAWER,
    minWidth,
    maxWidth,
    anchor: ANCHOR,
  });
  // if no fullHeight is requested, content should start below height of the app header
  // App header is overlapping by higher z-index, because dynamically added tabs cause height to
  // change from 64px to 113px
  const heightOffset = hasTabs ? 113 : 64; // TODO: Expose appbar height properly to ensure same height across app.
  const ToolbarOffset = !fullHeight ? (
    <Box sx={{ minHeight: heightOffset, height: heightOffset, maxHeight: heightOffset }} />
  ) : null;
  // const widthWithHandleOffset = useMemo(() => width, [width]);
  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant="persistent"
      anchor={ANCHOR}
      sx={(theme) => ({
        [`& .${drawerClasses.paper}`]: {
          width,
          minWidth,
          maxWidth,
          boxShadow: theme.shadows[2],
          // increasing zIndex on full height up to next zIndex
          // region (1300 = modal) to enforce full height visible
          zIndex: fullHeight ? theme.zIndex.drawer + 99 : theme.zIndex.drawer,
          // avoid scrolling behind overlapping header,
          // children need to specify scrolling explicitly
          overflow: 'hidden',
        },
      })}
    >
      {ToolbarOffset}
      {children}
      <DrawerResizeHandle isResizing={isResizing} enableResize={enableResize} />
    </Drawer>
  );
};
